import { Fragment, type ReactNode } from 'react';
import { RoleColumnHeaderCell } from './RoleColumnHeaderCell';
import { RoleMatrixCheckboxCells } from './RoleMatrixCheckboxCells';

// Shared role x feature/menu permission matrix table used by WebAccessPage and
// MobileAccessTab. Both pages rendered byte-identical table chrome: a sticky
// first column header, one <RoleColumnHeaderCell> <th> per role, then a tbody
// of groups. Each group is either a SINGLETON (one flat row whose label is the
// group label) or a MULTI group (an uppercase header row + indented child
// rows). The only true per-page differences were DATA (which groups/rows, how
// the checked flag is derived, which toggle fires) and a few web-only header
// bits (delete button, gap, count-label text, sticky-column min width), all
// exposed here as props. The previously-divergent COSMETIC differences (child
// label Tailwind class ORDER, and mobile's redundant colSpan={1} on the group
// header) are normalized to a single canonical rendering used by both pages.

/** A single matrix row: a stable key (menu id / feature key) + its display label. */
interface FeatureMatrixRow {
  key: string;
  label: string;
  /**
   * A small grey line under the label (Web Access: what the page's tick also allows,
   * e.g. "also: start an exit (files a resignation in 1Office)"). Omitted = no line.
   */
  note?: string;
}

/**
 * A matrix group. `rows.length === 1` renders a single flat row whose label is
 * the group `label` (a "singleton"). `rows.length > 1` renders an uppercase
 * group-header row (group `label`) followed by indented child rows (each
 * `row.label`).
 */
export interface FeatureMatrixGroup {
  /** Stable key for the group (React key + header row key). */
  key: string;
  /** Group display label (header text / singleton row label). */
  label: string;
  /** A small grey line under a multi group's header (what every row of it also allows). */
  note?: string;
  rows: FeatureMatrixRow[];
}

interface RoleLike {
  id: string;
  name: string;
  user_count?: number;
}

interface FeatureMatrixTableProps<R extends RoleLike> {
  /** Sticky first-column header text, e.g. "Menus/Roles" or "Mobile Features". */
  headerLabel: string;
  /** Tailwind min-width class for the sticky first column. Defaults to the web
   *  value `min-w-[200px]`; mobile passes `min-w-[220px]`. */
  headerColumnMinWidthClassName?: string;
  /** Role columns, in order. */
  roles: R[];
  /** The normalized matrix groups (singleton + multi). */
  groups: FeatureMatrixGroup[];
  /** Whether the given role currently has the given row's permission/feature. */
  isChecked: (roleId: string, rowKey: string) => boolean;
  /** Fired with (roleId, rowKey) when a checkbox cell is toggled. */
  onToggle: (roleId: string, rowKey: string) => void;
  /** Fired when a role column's edit (Pencil) button is clicked. */
  onEditRole: (role: R) => void;
  /** The pill count label content for a role column header. */
  countLabel: (role: R) => ReactNode;
  /** Optional extra action buttons after the edit button (web's delete button). */
  roleHeaderExtraActions?: (role: R) => ReactNode;
  /** Tailwind gap class for the role header action row. Defaults to `gap-1.5`. */
  roleHeaderActionRowGapClassName?: string;
}

export function FeatureMatrixTable<R extends RoleLike>({
  headerLabel,
  headerColumnMinWidthClassName = 'min-w-[200px]',
  roles,
  groups,
  isChecked,
  onToggle,
  onEditRole,
  countLabel,
  roleHeaderExtraActions,
  roleHeaderActionRowGapClassName,
}: FeatureMatrixTableProps<R>) {
  const renderCheckboxRow = (row: FeatureMatrixRow, indent: boolean) => (
    <tr key={row.key} className="bg-white hover:bg-primary-50/50 transition-colors">
      <td className="px-6 py-3.5 whitespace-nowrap sticky left-0 z-[1] border-r border-gray-100 bg-white">
        <span className={`text-sm ${indent ? 'pl-4 font-medium text-gray-800' : 'font-semibold text-gray-800'}`}>
          {row.label}
        </span>
        {row.note && (
          <p className={`mt-0.5 max-w-[280px] whitespace-normal text-[11px] leading-snug text-gray-500 ${indent ? 'pl-4' : ''}`}>
            {row.note}
          </p>
        )}
      </td>
      <RoleMatrixCheckboxCells
        roles={roles}
        isChecked={(roleId) => isChecked(roleId, row.key)}
        onToggle={(roleId) => onToggle(roleId, row.key)}
      />
    </tr>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* The table scrolls inside this box, both ways, so the row of role names stays at
          the top and the first column stays at the left however far a person scrolls.
          The box is never taller than the window less the top bar (two rows, about 7.5rem)
          and a margin, so the whole of it can be on screen at once. A header cell can
          only stay put inside the box that scrolls it: with the page doing the
          scrolling, the row of names used to slide away under the top bar. */}
      <div className="max-h-[calc(100vh-10rem)] overflow-auto">
        {/* border-separate: with collapsed borders a cell that stays put leaves its
            borders behind. */}
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {/* Solid fills on the header cells: rows pass underneath them. The corner
                  cell stays put both ways, so it sits above the other two. */}
              <th className={`px-6 py-4 text-left text-sm font-semibold text-gray-700 sticky left-0 top-0 z-20 bg-gray-50 ${headerColumnMinWidthClassName} border-b-2 border-gray-200`}>
                {headerLabel}
              </th>
              {roles.map((role) => (
                <th
                  key={role.id}
                  className="px-6 py-4 text-center min-w-[140px] border-b-2 border-gray-200 sticky top-0 z-10 bg-gray-50"
                >
                  <RoleColumnHeaderCell
                    name={role.name}
                    onEdit={() => onEditRole(role)}
                    countLabel={countLabel(role)}
                    userCount={role.user_count}
                    actionRowGapClassName={roleHeaderActionRowGapClassName}
                    extraActions={roleHeaderExtraActions?.(role)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              if (group.rows.length === 1) {
                return renderCheckboxRow({ key: group.rows[0].key, label: group.label, note: group.rows[0].note ?? group.note }, false);
              }

              return (
                <Fragment key={group.key}>
                  <tr className="bg-gray-100">
                    <td className="px-6 py-2 sticky left-0 z-[1] bg-gray-100 border-r border-gray-200">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-600">{group.label}</span>
                      {group.note && (
                        <p className="mt-0.5 max-w-[280px] text-[11px] leading-snug text-gray-500">{group.note}</p>
                      )}
                    </td>
                    <td colSpan={roles.length} className="bg-gray-100 border-b border-gray-200" />
                  </tr>
                  {group.rows.map((row) => renderCheckboxRow(row, true))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FeatureMatrixTable;
